import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faCalendarAlt,
  faClock,
  faComment,
  faDownload,
  faEye,
  faFile,
  faFileImage,
  faFilePdf,
  faFlag,
  faForward,
  faPaperclip,
  faPaperPlane,
  faReply,
  faSpinner,
  faUser,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import { buildMediaUrl, getCurrentUserIdentity } from "../Tickets/ticketUtils";
import { addTaskReply, getTaskById } from "./taskService";
import {
  formatTaskDate,
  getTaskPriorityClasses,
  getTaskStatusClasses,
} from "./taskUtils";

const BRAND_LR = "linear-gradient(to right, #C04C2A, #4A1B13)";

const MAX_FILE_SIZE = 20 * 1024 * 1024;

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fileIconFor = (name = "") => {
  if (/\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name)) return faFileImage;
  if (/\.pdf$/i.test(name)) return faFilePdf;
  return faFile;
};

function FileChip({ file }) {
  return (
    <a
      href={buildMediaUrl(file.fileUrl)}
      target="_blank"
      rel="noreferrer"
      download={file.fileName}
      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs transition hover:border-orange-300 dark:border-gray-700 dark:bg-gray-800"
    >
      <FontAwesomeIcon icon={fileIconFor(file.fileName)} className="text-slate-400" />
      <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-gray-200">{file.fileName}</span>
      <span className="shrink-0 text-slate-400">{formatFileSize(file.fileSize)}</span>
      <FontAwesomeIcon icon={faDownload} className="shrink-0 text-slate-400" />
    </a>
  );
}

export default function TaskDetailsPage() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const identity = useMemo(() => getCurrentUserIdentity(), []);

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [replyType, setReplyType] = useState("Reply");
  const [replyMessage, setReplyMessage] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [replyError, setReplyError] = useState("");
  const [sending, setSending] = useState(false);

  const loadTask = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getTaskById(taskId);
      setTask(data);
    } catch (err) {
      setError(err?.message || "Task details could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  const handleFilesPicked = (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    const tooLarge = picked.filter((f) => f.size > MAX_FILE_SIZE);
    const accepted = picked.filter((f) => f.size <= MAX_FILE_SIZE);
    setReplyError(tooLarge.length ? `${tooLarge.map((f) => f.name).join(", ")} exceed(s) the 20MB limit and were skipped.` : "");
    if (accepted.length) setReplyFiles((cur) => [...cur, ...accepted]);
  };

  const removeReplyFile = (idx) => setReplyFiles((cur) => cur.filter((_, i) => i !== idx));

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      setReplyError("Write a message before sending.");
      return;
    }
    setSending(true);
    setReplyError("");
    try {
      await addTaskReply(taskId, { type: replyType, message: replyMessage.trim(), files: replyFiles });
      setReplyMessage("");
      setReplyFiles([]);
      await loadTask();
    } catch (err) {
      setReplyError(err?.message || "Reply could not be sent.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl" style={{ color: "#C04C2A" }} />
      </div>
    );
  }

  if (error || !task) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Breadcrumb title="Task Details" />
        <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-6 py-8 text-center dark:border-rose-900/40 dark:bg-rose-900/10">
          <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">{error || "Task not found."}</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-4 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:bg-gray-800 dark:text-rose-300"
          >
            <FontAwesomeIcon icon={faAngleLeft} /> Go Back
          </button>
        </div>
      </div>
    );
  }

  const forwardEntries = (task.history || []).filter((h) => h.actionType === "Forwarded");

  const currentUserId = String(identity?.userId || "");
  const canReply = Boolean(
    currentUserId &&
      (String(task.assignedUserId || "") === currentUserId ||
        String(task.createdByUserId || "") === currentUserId ||
        (task.observers || []).some((o) => String(o.userId || "") === currentUserId))
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <Breadcrumb title="Task Details" />

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-orange-600 dark:text-gray-400"
      >
        <FontAwesomeIcon icon={faAngleLeft} /> Back to tasks
      </button>

      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white px-5 py-5 text-black shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:text-white sm:px-7 sm:py-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              {task.ticketNumber ? `Ticket ${task.ticketNumber}` : "Manual Task"}
            </div>
            <h1 className="mt-2 text-xl font-extrabold tracking-tight sm:text-2xl">{task.title}</h1>
            <p className="mt-1 max-w-2xl whitespace-pre-line text-sm text-slate-600 dark:text-gray-300">{task.description}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <span className={`rounded-lg px-2.5 py-1 text-[11px] font-bold ${getTaskStatusClasses(task.status)}`}>{task.status}</span>
            <span className={`rounded-lg px-2.5 py-1 text-[11px] font-bold ${getTaskPriorityClasses(task.priority)}`}>
              <FontAwesomeIcon icon={faFlag} className="mr-1 text-[9px]" />
              {task.priority}
            </span>
          </div>
        </div>

        <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-gray-800">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${task.progressPercent}%`, background: task.progressPercent === 100 ? "linear-gradient(to right,#34d399,#10b981)" : BRAND_LR }}
          />
        </div>
        <p className="mt-1 text-right text-[10px] text-slate-500 dark:text-gray-400">{task.progressPercent}% complete</p>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Left: main content */}
        <div className="space-y-5 lg:col-span-2">
          {/* Task Info */}
          <Section title="Task Information" icon={faUser}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoRow label="Assigned To" value={task.assignedEmployeeName} />
              <InfoRow label="Created By" value={task.createdByName} />
              <InfoRow label="Due Date" value={formatTaskDate(task.dueDate)} icon={faCalendarAlt} />
              <InfoRow label="Last Updated" value={formatTaskDate(task.updatedAt, true)} icon={faClock} />
            </div>
          </Section>

          {/* Forwarding info */}
          {forwardEntries.length > 0 && (
            <Section title="Forwarding History" icon={faForward}>
              <div className="space-y-2">
                {forwardEntries.map((h) => (
                  <div key={h.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-xs dark:border-gray-700 dark:bg-gray-800/50">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-700 dark:text-gray-200">
                        {h.fromAssignedEmployeeName || "—"} <FontAwesomeIcon icon={faAngleLeft} className="mx-1 rotate-180 text-[9px] text-slate-400" /> {h.toAssignedEmployeeName || "—"}
                      </span>
                      <span className="text-[10px] text-slate-400">{formatTaskDate(h.createdAt, true)}</span>
                    </div>
                    {h.note && <p className="mt-1 text-slate-500 dark:text-gray-400">{h.note}</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Original attachments */}
          {task.files.length > 0 && (
            <Section title="Attachments" icon={faPaperclip}>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {task.files.map((f) => <FileChip key={f.id} file={f} />)}
              </div>
            </Section>
          )}

          {/* Replies & Comments thread */}
          <Section title="Replies & Comments" icon={faComment}>
            <div className="space-y-3">
              {task.replies.length === 0 && (
                <p className="text-xs text-slate-400">No replies or comments yet. Start the conversation below.</p>
              )}
              {task.replies.map((reply) => (
                <div key={reply.id} className="rounded-xl border border-slate-100 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800/60">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white"
                        style={{ background: BRAND_LR }}
                      >
                        {(reply.authorName || "U").slice(0, 2).toUpperCase()}
                      </span>
                      <span className="text-xs font-semibold text-slate-700 dark:text-gray-200">{reply.authorName}</span>
                      {(task.observers || []).some((o) => o.userId && o.userId === reply.authorUserId) && (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
                          <FontAwesomeIcon icon={faEye} className="mr-1 text-[8px]" />
                          Observer
                        </span>
                      )}
                      <span
                        className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                          reply.type === "Comment"
                            ? "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                            : "bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300"
                        }`}
                      >
                        <FontAwesomeIcon icon={reply.type === "Comment" ? faComment : faReply} className="mr-1 text-[8px]" />
                        {reply.type}
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-400">{formatTaskDate(reply.createdAt, true)}</span>
                  </div>
                  <p className="mt-2 whitespace-pre-line text-xs text-slate-600 dark:text-gray-300">{reply.message}</p>
                  {reply.files.length > 0 && (
                    <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                      {reply.files.map((f) => <FileChip key={f.id} file={f} />)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Composer */}
            {canReply ? (
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                <div className="mb-2 flex gap-1.5">
                  {["Reply", "Comment"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setReplyType(t)}
                      className={`rounded-lg px-3 py-1 text-xs font-bold transition ${
                        replyType === t ? "text-white" : "border border-slate-200 bg-white text-slate-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
                      }`}
                      style={replyType === t ? { background: BRAND_LR } : undefined}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  rows={3}
                  placeholder={replyType === "Comment" ? "Add an internal comment…" : "Write a reply…"}
                  className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs outline-none focus:border-orange-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                />

                {replyError && <p className="mt-1 text-[11px] font-medium text-rose-600">{replyError}</p>}

                {replyFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {replyFiles.map((file, idx) => (
                      <div key={`${file.name}-${idx}`} className="flex items-center gap-2 rounded-lg border border-orange-100 bg-orange-50/40 px-2 py-1 text-xs dark:border-orange-900/30 dark:bg-orange-900/10">
                        <FontAwesomeIcon icon={fileIconFor(file.name)} style={{ color: "#C04C2A" }} />
                        <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-gray-200">{file.name}</span>
                        <span className="shrink-0 text-slate-400">{formatFileSize(file.size)}</span>
                        <button type="button" onClick={() => removeReplyFile(idx)} className="text-slate-400 hover:text-rose-600">×</button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-2 flex items-center justify-between">
                  <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-slate-500 hover:text-orange-600 dark:text-gray-400">
                    <FontAwesomeIcon icon={faPaperclip} />
                    Attach files
                    <input type="file" multiple className="hidden" onChange={(e) => { handleFilesPicked(e.target.files); e.target.value = ""; }} />
                  </label>
                  <button
                    type="button"
                    onClick={handleSendReply}
                    disabled={sending}
                    className="inline-flex items-center gap-2 rounded-lg px-4 py-1.5 text-xs font-bold text-white shadow-sm transition hover:opacity-90 disabled:opacity-60"
                    style={{ background: BRAND_LR }}
                  >
                    <FontAwesomeIcon icon={sending ? faSpinner : faPaperPlane} spin={sending} />
                    {sending ? "Sending…" : `Send ${replyType}`}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-center text-[11px] text-slate-400 dark:border-gray-700 dark:bg-gray-800/50">
                Only the task owner, assignee, or an observer can reply or comment on this task.
              </div>
            )}
          </Section>
        </div>

        {/* Right: observers + meta */}
        <div className="space-y-5">
          <Section title="Observers" icon={faEye}>
            {task.observers.length === 0 ? (
              <p className="text-xs text-slate-400">No observers on this task.</p>
            ) : (
              <div className="space-y-1.5">
                {task.observers.map((o) => (
                  <div key={o.id} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-1.5 dark:border-gray-700 dark:bg-gray-800/50">
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                      style={{ background: BRAND_LR }}
                    >
                      {(o.employeeName || "U").slice(0, 2).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-semibold text-slate-700 dark:text-gray-200">{o.employeeName}</div>
                      <div className="text-[10px] text-slate-400">
                        {o.source === "Forward" || o.source === "ForwardSync" ? "Added via forward" : "Added manually"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Timeline" icon={faClock}>
            <div className="space-y-2">
              {(task.history || []).slice(0, 8).map((h) => (
                <div key={h.id} className="border-l-2 pl-2.5 text-xs" style={{ borderColor: "rgba(192,76,42,0.3)" }}>
                  <div className="font-semibold text-slate-700 dark:text-gray-200">{h.actionLabel}</div>
                  <div className="text-[10px] text-slate-400">{formatTaskDate(h.createdAt, true)} · {h.actorName}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="People" icon={faUserCheck}>
            <InfoRow label="Assigned To" value={task.assignedEmployeeName} stacked />
            <InfoRow label="Created By" value={task.createdByName} stacked />
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-5">
      <h2 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-gray-400">
        <FontAwesomeIcon icon={icon} style={{ color: "#C04C2A" }} />
        {title}
      </h2>
      {children}
    </div>
  );
}

function InfoRow({ label, value, icon, stacked }) {
  return (
    <div className={stacked ? "mb-2 last:mb-0" : ""}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</div>
      <div className="mt-0.5 flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-gray-200">
        {icon && <FontAwesomeIcon icon={icon} className="text-[10px] text-slate-400" />}
        {value || "—"}
      </div>
    </div>
  );
}
